import type { SchemaTemplate } from "@/types/schemaBuilder";

interface SchemaTemplateOptionCardProps {
  template: SchemaTemplate;
  selected?: boolean;
  onClick?: () => void;
  compact?: boolean;
}

export function SchemaTemplateOptionCard({
  template,
  selected = false,
  onClick,
  compact = false,
}: SchemaTemplateOptionCardProps) {
  const isPublished = template.status === "published";

  const accentClass = isPublished ? "border-t-cyan-500" : "border-t-green-500";
  const badgeClass = isPublished
    ? "border-cyan-500 text-cyan-700"
    : "border-green-500 text-green-700";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex h-full w-full flex-col rounded-md border border-[var(--color-border-light)] border-t-4 bg-white p-4 text-left shadow-sm transition ${accentClass} ${
        selected
          ? "border-[var(--color-action-primary)] border-t-[var(--color-action-primary)]"
          : "hover:border-[var(--color-action-primary)]"
      }`}
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <h3
          title={template.name}
          className="min-w-0 truncate text-sm font-semibold text-[var(--color-text-primary)]"
        >
          {template.name}
        </h3>

        <span
          className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium ${badgeClass}`}
        >
          {isPublished ? "Published" : "Preset"}
        </span>
      </div>

      {template.description && (
        <p className="mb-4 text-xs leading-5 text-[var(--color-text-secondary)]">
          {template.description}
        </p>
      )}

      <div className="border-t border-[var(--color-border-light)] pt-3">
        <h4 className="mb-2 text-xs font-semibold text-[var(--color-text-primary)]">Structure</h4>

        <div className="space-y-1 text-xs text-[var(--color-text-secondary)]">
          <div className="flex justify-between">
            <span>Total Columns</span>
            <span>{template.totalColumns}</span>
          </div>

          <div className="flex justify-between">
            <span>Rules</span>
            <span>
              {template.rules}{" "}
              {template.crossColumnRules ? `(${template.crossColumnRules} cross-column)` : ""}
            </span>
          </div>

          <div className="flex justify-between">
            <span>Known Mappings</span>
            <span>{template.knownMappings}</span>
          </div>
        </div>
      </div>

      {!compact && selected && (
        <div className="mt-3 text-xs font-medium text-[var(--color-action-primary)]">Selected</div>
      )}
    </button>
  );
}
